import { style } from '@angular/animations';
import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { wesethLogoTransLogo } from 'src/assets/assets-base-64';

// Attach the font virtual-file-system once on the client side
(pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;

/* ----------------------------------------------------------------------
 *  Types & Interfaces
 * --------------------------------------------------------------------*/
export interface PdfTableOptions {
  /** Max recursion depth when converting JSON → table rows */
  maxDepth?: number;
}

/* ----------------------------------------------------------------------
 *  Helper function to create colored circle
 * --------------------------------------------------------------------*/
function createColoredCircle(color: string): any {
  // Map color names to hex values
  const colorMap: { [key: string]: string } = {
    'red': '#FF0000',
    'green': '#00FF00',
    'blue': '#0000FF',
    'yellow': '#FFFF00',
    'orange': '#FFA500',
    'purple': '#800080',
    'black': '#000000',
    'gray': '#808080',
    'grey': '#808080'
  };
  
  const fillColor = colorMap[color.toLowerCase()] || color;
  let resultText = '';
  const colorLower = color.toLowerCase();
  if (colorLower === 'green') {
    resultText = ' Passed ';
  } else if (colorLower === 'red') {
    resultText = ' Failed ';
  }
  return {
    columns: [
      {
        text: resultText,
        width: 'auto'
      },
      {
        canvas: [
          {
            type: 'ellipse',
            x: 5,
            y: 5,
            r1: 5,
            r2: 5,
            color: fillColor
          }
        ],
        width: 15,
        height: 10
      }
    ],
    columnGap: 5
  };
}

/* ----------------------------------------------------------------------
 *  Recursive JSON → pdfmake node converter
 * --------------------------------------------------------------------*/
export function renderNode(
  data: unknown,
  opts: PdfTableOptions = {},
  depth = 0,
  parentKey?: string 
): any {
  if (opts.maxDepth != null && depth > opts.maxDepth) return JSON.stringify(data);

  
  if (parentKey === 'result' && typeof data === 'string') {
    return createColoredCircle(data); 
  }

  // Primitive → plain text ------------------------------------------------
  if (
    data === null ||
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  ) {
    return String(data);
  }

  // Array → 2-column table (index | value) -------------------------------
  if (Array.isArray(data)) {
    const body = data.map((item, i) => [
      { text: String(i), bold: true },
      renderNode(item, opts, depth + 1), 
    ]);
    return { table: { body } };
  }

  // Object → 2-column table (key | value) --------------------------------
  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>);
    const body = entries.map(([key, value]) => [
      { text: key, bold: true },
      renderNode(value, opts, depth + 1, key), 
    ]);
    return { table: { body } };
  }

  // Fallback  --------------------------------------
  return String(data);
}

/* ----------------------------------------------------------------------
 *  Build the first-level (root) content of a section
 * --------------------------------------------------------------------*/
function buildRootContent(
  json: Record<string, unknown>,
  opts: PdfTableOptions
): any[] {
  const content: any[] = [];
  
  const boldFields = new Set(['completed_at', 'created_at', 'result', 'description', 'title']);

  Object.entries(json).forEach(([key, value]) => {
    const isPrimitive =
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean';

    // recognize special keys
    const keyLower = key.toLowerCase();
    const isInput = keyLower === 'input';
    const isOutput = keyLower === 'output';

    if (isPrimitive) {
      
      if (key === 'result' && typeof value === 'string') {
        content.push({
          columns: [
            { text: `${key}:`, bold: true, width: 'auto' },
            createColoredCircle(value)
          ],
          margin: [0, 0, 0, 8]
        });
      } else if ((key === 'created_at' || key === 'completed_at') && typeof value === 'string') {
        const date = new Date(value);
        let displayValueDate = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        const valueTextDate = { text: ` ${String(displayValueDate)}` };
        const marginBottom = boldFields.has(key) ? 8 : 2;
        content.push({
          text: [{ text: `${key}:`, bold: true }, valueTextDate],
          margin: [0, 0, 0, marginBottom]
        });
      } else {
        const keyText = boldFields.has(key) ? { text: `${key}:`, bold: true } : { text: `${key}:` };
        const valueText = { text: ` ${String(value)}` };
        const marginBottom = boldFields.has(key) ? 8 : 2;
        content.push({
          text: [keyText, valueText],
          margin: [0, 0, 0, marginBottom]
        });
      }
    } else {
      // --- TABLE SECTIONS ---

      // page break before OUTPUT
      if (isOutput) {
        content.push({ text: '', pageBreak: 'before' });
      }

      const titleStyle = boldFields.has(key) ? { bold: true } : {};
      const marginBottom = boldFields.has(key) ? 8 : 4;
      const marginTop = boldFields.has(key) ? 12 : 8;

      // Title section
      content.push({ 
        text: key, 
        style: 'subheader', 
        margin: [0, marginTop, 0, marginBottom],
        ...titleStyle
      });

      
      if (isInput) {
        content.push({
          text: 'The following table reports the input data used for the test.',
          margin: [0, 0, 0, 8],
          italics: true
        });
      }
      if (isOutput) {
        content.push({
          text: 'The following table reports the output data obtained at the end of the test.',
          margin: [0, 0, 0, 8],
          italics: true
        });
      }

      content.push(renderNode(value, opts, 1, key));
    }
  });

  return content;
}

const buildHeader = () => ({
  margin: [20, 0, 20, 0], // Align top margin with body & footer
  columns: [
    { image: wesethLogoTransLogo, width: 140 },
  ],
});

const buildFooter = (currentPage: number, pageCount: number) => ({
  margin: [40, 0, 40, 20],
  stack: [
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 0.5,
        },
      ],
    },
    {
      text: ' CONFIDENTIAL INFORMATION ',
      style: 'footer',
      margin: [0, 4, 0, 0],
      alignment: 'center',
    },
    {
      text: `© ${new Date().getFullYear()} DRIVESEC - This document, which is supplied in confidence together with its contents is the copyright property of DRIVESEC. Neither the whole nor any extract may be disclosed, loaned or copied, nor shall it be used, for any purpose other than those purposes for which written permission was given at the time of release, except to the extent, and for the purposes, specifically approved in writing by DRIVESEC. This document is shared under Non-Disclosure Agreement.`,
      style: 'footer',
    },
    {
      text: `Page ${currentPage} of ${pageCount}`,
      margin: [0, 4, 0, 0],
      alignment: 'center',
      style: 'footer'
    }
  ],
});

/* ----------------------------------------------------------------------
 *  DocDefinition creators
 * --------------------------------------------------------------------*/
export function jsonToPdfDoc(
  json: unknown,
  title = 'Report',
  opts: PdfTableOptions = {}
): any {
  const content: any[] = [{ text: title, style: 'header', margin: [0, 12, 0, 12] }];

  if (typeof json === 'object' && !Array.isArray(json) && json !== null) {
    content.push(...buildRootContent(json as Record<string, unknown>, opts));
  } else {
    content.push(String(json));
  }

  return {
    info: { title },
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 60],
    header: buildHeader,
    footer: buildFooter,
    content,
    styles: {
      header: { fontSize: 18, bold: true },
      subheader: { fontSize: 12, bold: true },
      footer: { fontSize: 7, color: '#666' },
    },
    defaultStyle: { fontSize: 10 },
  };
}

/* ----------------------------------------------------------------------
 *  Create a multi-section (merged) DocDefinition
 * --------------------------------------------------------------------*/
export function buildMergedPdfDoc(
  dataArray: unknown[],
  titles: string[] = [],
  mergedTitle = 'Merged Report',
  opts: PdfTableOptions = {}
): any {
  if (!dataArray.length) throw new Error('dataArray must contain at least one element');

  const mergedContent: any[] = [];

  dataArray.forEach((data, idx) => {
    if (idx > 0) mergedContent.push({ text: '', pageBreak: 'before' });

    const localTitle = titles[idx] ?? `Report ${idx + 1}`;
    mergedContent.push({ text: localTitle, style: 'header', margin: [0, 0, 0, 12] });

    if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
      mergedContent.push(...buildRootContent(data as Record<string, unknown>, opts));
    } else {
      mergedContent.push(String(data));
    }
  });

  return {
    info: { title: mergedTitle },
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 60],
    header: buildHeader,
    footer: buildFooter,
    content: mergedContent,
    styles: {
      header: { fontSize: 18, bold: true },
      subheader: { fontSize: 12, bold: true },
      footer: { fontSize: 7, color: '#666' },
    },
    defaultStyle: { fontSize: 10 },
  };
}

/* ----------------------------------------------------------------------
 *  Service
 * --------------------------------------------------------------------*/
@Injectable({ providedIn: 'root' })
export class PdfReportService {
  /** Helper that bridges createPdf → Blob promise */
  private createPdfBlob(definition: any): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      (pdfMake as any).createPdf(definition).getBlob(resolve, reject);
    });
  }

  /** Single document – returns a Blob */
  generatePdfBlob(
    data: unknown,
    title = 'Report',
    opts: PdfTableOptions = {}
  ): Promise<Blob> {
    return this.createPdfBlob(jsonToPdfDoc(data, title, opts));
  }

  /** Single document – triggers browser download */
  downloadPdf(
    data: unknown,
    fileName = 'report.pdf',
    title = 'Report',
    opts: PdfTableOptions = {}
  ): void {
    (pdfMake as any).createPdf(jsonToPdfDoc(data, title, opts)).download(fileName);
  }

  /* -------------------- MULTIPLE MERGE ---------------------------- */
  /** Multi-section document – returns a Blob */
  generateMergedPdfBlob(
    dataArray: unknown[],
    titles: string[] = [],
    mergedTitle = 'Merged Report',
    opts: PdfTableOptions = {}
  ): Promise<Blob> {
    return this.createPdfBlob(buildMergedPdfDoc(dataArray, titles, mergedTitle, opts));
  }

  /** Multi-section document – triggers browser download */
  downloadMergedPdf(
    dataArray: unknown[],
    fileName = 'merged-report.pdf',
    titles: string[] = [],
    mergedTitle = 'Merged Report',
    opts: PdfTableOptions = {}
  ): void {
    this.generateMergedPdfBlob(dataArray, titles, mergedTitle, opts)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement('a'), {
          href: url,
          download: fileName,
        });
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(console.error);
  }
}